import React, { useContext, useEffect, useMemo, useState, Fragment } from "react";
import { useParams } from "react-router-dom";
import { GlobalContext } from "../../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";

import {
  Calendar,
  Download,
  PlayCircle,
  RefreshCcw,
  CheckCircle2,
  Clock4,
  Smartphone,
  Banknote,
  AlertTriangle,
  UtensilsCrossed,   // NEW
  Briefcase,         // NEW
  Home,              // NEW
  CarFront           // NEW
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import useSettlement from "../../Services/OrderServices/SettlementServices/useSettlement";

const ChaafoDetails = {
  name: "Chaafo",
  gst: "09AAKCC2923R1Z1",
  Chaafotagline: "Order Managed By Chaafo",
  chaafoImage: "https://anlook.com/images/chaafo.png",
};


/* Equal-width icon pill */
const Pill = ({ color = "slate", icon: Icon = CheckCircle2, children }) => {
  const map = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    yellow: "bg-amber-50 text-amber-700 border-amber-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <span
      className={`inline-flex h-6 w-32 items-center justify-center gap-1.5 rounded-full border px-2 text-[11px] font-semibold ${map[color]}`}
    >
      <Icon size={14} /> {children}
    </span>
  );
};

const Confirm = ({
  open,
  title,
  desc,
  confirmText = "Confirm",
  danger = false,
  onClose,
  onConfirm,
}) => (
  <Transition show={open} as={Fragment}>
    <Dialog onClose={onClose} className="relative z-50">
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/40" />
      </Transition.Child>
      <div className="fixed inset-0 grid place-items-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <Dialog.Title className="text-base font-semibold">
              {title}
            </Dialog.Title>
            {desc ? (
              <p className="mt-2 text-sm text-gray-600">{desc}</p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="h-9 rounded-xl border px-3 text-sm"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className={`h-9 rounded-xl px-4 text-sm font-medium text-white ${danger ? "bg-rose-600 hover:bg-rose-700" : "bg-[#01C37D]"
                  }`}
                onClick={onConfirm}
              >
                {confirmText}
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition>
);

const fmt2 = (n) => (Number.isFinite(+n) ? (+n).toFixed(2) : "0.00");

// NEW: helper to get yesterday's date in YYYY-MM-DD
const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
};

// NEW: map for order type UI
const ORDER_TYPES = [
  {
    key: "takeaway",
    label: "Takeaway",
    icon: UtensilsCrossed,
    desc: "Counter pickup orders",
  },
  {
    key: "office_order",
    label: "Office Order",
    icon: Briefcase,
    desc: "Corporate / office orders",
  },
  {
    key: "home_delivery",
    label: "Home Delivery",
    icon: Home,
    desc: "Delivered to customer home",
  },
  {
    key: "car_order",
    label: "Car Order",
    icon: CarFront,
    desc: "Drive-in / car service",
  },
];

export default function AdminSettlementAnalytics() {
  const {getTransactionsAnalytics, getSettlementReport, getProcessedSettlementsAdmin, processSettlement, processOrderTypeSettlement}=useSettlement();

  const { clientId, clientName } = useParams();
  const { api } = useContext(GlobalContext);

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const yesterday = useMemo(() => getYesterday(), []);

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [analytics, setAnalytics] = useState(null);
  const [report, setReport] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const [confirmProcess, setConfirmProcess] = useState(false);
  const [confirmReportDownload, setConfirmReportDownload] = useState(false);
  const [confirmHistoryDownload, setConfirmHistoryDownload] = useState(false);

  /* New tabbing for tables */
  const [activeTab, setActiveTab] = useState("report"); // "report" | "history"

  // NEW: order-type settlement modal state
  const [orderTypeModalOpen, setOrderTypeModalOpen] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState("takeaway");

  const pullAnalytics = async () => {
  if (!clientId) return;

  try {
    const result =
      await getTransactionsAnalytics({
        userid: clientId,
        fromDate,
        toDate,
      });

    if (result.success) {
      setAnalytics(result.data);
    } else {
      setAnalytics(null);
    }
  } catch {
    setAnalytics(null);
  }
};

 const pullReport = async () => {
  if (!clientId) return;

  setLoading(true);

  try {
    const result =
      await getSettlementReport({
        userid: clientId,
        fromDate,
        toDate,
      });

    if (result.success) {
      setReport(result.data);
    } else {
      setReport([]);
    }
  } catch {
    setReport([]);
  } finally {
    setLoading(false);
  }
};


 const pullHistory = async () => {
  if (!clientId) return;

  try {
    const result =
      await getProcessedSettlementsAdmin({
        userid: clientId,
        fromDate,
        toDate,
      });

    if (result.success) {
      setHistory(result.data);
    } else {
      setHistory([]);
    }
  } catch {
    setHistory([]);
  }
};


  useEffect(() => {
    pullReport();
    pullHistory();
    // eslint-disable-next-line
  }, [clientId]);

  const applyFilter = () => {
    if (new Date(fromDate) > new Date(toDate)) {
      toast.error("From Date cannot be after To Date.");
      return;
    }
    pullAnalytics();
    pullReport();
    pullHistory();
  };

const processSettlementHandler = async () => {
  setConfirmProcess(false);
  setLoading(true);

  try {
    const result =
      await processSettlement({
        userid: clientId,
        fromDate,
        toDate,
      });

    if (result.success) {
      toast.success(result.message);
      pullAnalytics();
      pullReport();
      pullHistory();
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Error processing settlement.");
  } finally {
    setLoading(false);
  }
};


  // NEW: v2.0 order-type based settlement for yesterday only
 const processOrderTypeSettlementHandler = async () => {
  if (!selectedOrderType) {
    toast.error("Please select an order type.");
    return;
  }

  setOrderTypeModalOpen(false);
  setLoading(true);

  try {
    const result =
      await processOrderTypeSettlement({
        userid: clientId,
        orderType: selectedOrderType,
        fromDate: "",
        toDate: yesterday,
      });

    if (result.success) {
      toast.success(result.message);
      pullAnalytics();
      pullReport();
      pullHistory();
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error(
      "Error processing order-type settlement."
    );
  } finally {
    setLoading(false);
  }
};


  const downloadExcel = (rows, name) => {
    if (!rows.length) {
      toast.error("No data to download.");
      return;
    }
    try {
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        `${name}_${clientName || clientId}_${fromDate}_${toDate}.xlsx`
      );
      toast.success("Excel generated.");
    } catch {
      toast.error("Failed to generate Excel.");
    }
  };
const downloadSettlementInvoice = (inv) => {
  const safe = (v) => (v === null || v === undefined ? "" : String(v));

  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const money = (v) => num(v).toFixed(2);

  // -------- CLIENT DETAILS --------
  const clientName = safe(inv.organization_name) || "Client";
  const clientGST = safe(inv.gst) || "-";
  const clientLogo = safe(inv.organization_logo);

  // -------- INVOICE META --------
  const invoiceNumber = safe(inv.id); // ✅ use id as invoice number
  const fromDate = safe(inv.from_date);
  const toDate = safe(inv.to_date);
  const generatedOn = safe(inv.created_on);

  // -------- AMOUNTS --------
  const originalOrderAmount = num(inv.orderAmount);
  const deliveryCharges = num(inv.deliveryCharges);

  // ✅ subtract delivery charges silently
  const orderAmount = Math.max(0, originalOrderAmount - deliveryCharges);

  const platformCharge = num(inv.platformCharges);
  const messagingCharge = num(inv.messageCharges);

  const platformChargeTax = 0;
  const totalFoodGst = 0;

  const grossAmount = orderAmount;
  const settlementAmount = num(inv.amount);

  // -------- CHAAFO --------
  const ChaafoDetails = {
    name: "Chaafo",
    gst: "09AAKCC2923R1Z1",
    Chaafotagline: "Order Managed By Chaafo",
    chaafoImage: "https://anlook.com/images/chaafo.png",
  };

  const summaryText = `Settlement invoice for ${clientName} for period ${fromDate} to ${toDate}. 
  Order amount ${money(orderAmount)}, deductions applied as below, final settlement amount ${money(settlementAmount)}.`;

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<title>Settlement Invoice ${invoiceNumber}</title>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>
  *{box-sizing:border-box}
  body{margin:0;font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial;background:#fff;color:#0f172a}
  .page{width:840px;margin:0 auto;padding:26px 24px 40px;position:relative;z-index:1}

  /* WATERMARK */
  .watermark{
    position:fixed;
    inset:0;
    display:flex;
    align-items:center;
    justify-content:center;
    pointer-events:none;
    z-index:0;
  }
  .watermark img{
    width:75%;
    max-width:760px;
    opacity:0.06;
    filter:grayscale(100%);
  }

  .header{display:flex;justify-content:space-between;gap:18px}
  .block{display:flex;gap:12px}
  .logoBox{width:44px;height:44px;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0}
  .logoBox img{width:100%;height:100%;object-fit:cover}
  .chaafoLogo{height:28px;object-fit:contain}

  .hTitle{font-weight:900;font-size:14px}
  .hMeta{font-size:12px;color:#334155}

  .topBar{display:flex;justify-content:space-between;margin:18px 0}
  .title{font-size:22px;font-weight:950}
  .pill{border:1px solid #e2e8f0;padding:8px 12px;border-radius:999px;font-size:12px}

  .summary{border:1px solid #e2e8f0;background:#f8fafc;border-radius:14px;padding:12px}
  .summary h3{margin:0 0 4px;font-size:13px}
  .summary p{margin:0;font-size:12.5px;color:#334155}

  .grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:12px}
  .kv{border:1px solid #e2e8f0;border-radius:14px;padding:12px}
  .kv .k{font-size:11px;color:#64748b}
  .kv .v{font-size:13px;font-weight:900}

  table{width:100%;border-collapse:collapse;margin-top:14px;border:1px solid #e2e8f0}
  th,td{padding:10px 12px;font-size:12.5px;border-bottom:1px solid #eef2f7}
  th{background:#f1f5f9;text-align:left}
  td.num{text-align:right}

  .sumBox{display:grid;grid-template-columns:1fr 320px;gap:14px;margin-top:14px}
  .totals{border:1px solid #e2e8f0;border-radius:14px;padding:12px}
  .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px dashed #e2e8f0}
  .row:last-child{border-bottom:none}
  .grand{font-size:14px;font-weight:900}

  .footer{text-align:center;margin-top:16px;font-size:12px;color:#475569}

  .termsWrap{margin-top:18px;border-top:1px solid #e2e8f0;padding-top:12px}
  .termsTitle{font-size:13px;font-weight:900}
  .termsText{font-size:12px;color:#334155;line-height:1.45}
</style>
</head>

<body>
<div class="watermark">
  <img src="${ChaafoDetails.chaafoImage}" />
</div>

<div class="page">

  <div class="header">
    <div class="block">
      <div class="logoBox">${clientLogo ? `<img src="${clientLogo}"/>` : ""}</div>
      <div>
        <div class="hTitle"><b>Restaurant Name</b> ${clientName}</div>
        <div class="hMeta">GST No: <b>${clientGST}</b></div>
        <div class="hMeta">${fromDate} → ${toDate}</div>
      </div>
    </div>

    <div class="block">
      <div style="text-align:right">
        <div class="hTitle">${ChaafoDetails.name}</div>
        <div class="hMeta">Invoice No: <b>${invoiceNumber}</b></div>
        <div class="hMeta">GST No: <b>${ChaafoDetails.gst}</b></div>
        <div class="hMeta">Generated: ${generatedOn}</div>
      </div>
      <img src="${ChaafoDetails.chaafoImage}" class="chaafoLogo"/>
    </div>
  </div>

  <div class="topBar">
    <div>
      <div class="title">Settlement Invoice</div>
      <div class="hMeta">Summary and detailed deduction breakdown</div>
    </div>
    <div class="pill">Status: <b style="color:#16a34a">SETTLED</b></div>
  </div>

  <div class="summary">
    <h3>Summary</h3>
    <p>${summaryText}</p>
  </div>

  <div class="grid">
    <div class="kv"><div class="k">Settlement Period</div><div class="v">${fromDate} → ${toDate}</div></div>
    <div class="kv"><div class="k">Invoice Number</div><div class="v">${invoiceNumber}</div></div>
  </div>

  <table>
    <thead>
      <tr><th>#</th><th>Particulars</th><th class="num">Amount</th></tr>
    </thead>
    <tbody>
      <tr><td>1</td><td>Order Amount</td><td class="num">${money(orderAmount)}</td></tr>
    
      <tr><td>3</td><td>Platform Charge</td><td class="num">-${money(platformCharge)}</td></tr>
      <tr><td>4</td><td>Platform Charge Tax</td><td class="num">-${money(platformChargeTax)}</td></tr>
      <tr><td>5</td><td>Messaging Charge</td><td class="num">-${money(messagingCharge)}</td></tr>
      <tr><td>6</td><td>Total Food GST</td><td class="num">-${money(totalFoodGst)}</td></tr>
    </tbody>
  </table>

  <div class="sumBox">
    <div class="totals">
      <div class="row"><span>Order Amount</span><span>${money(orderAmount)}</span></div>
      <div class="row"><span>Platform Charge</span><span>-${money(platformCharge)}</span></div>
      <div class="row"><span>Messaging Charge</span><span>-${money(messagingCharge)}</span></div>
      <div class="row grand"><span>Settlement Amount</span><span>${money(settlementAmount)}</span></div>
    </div>

    <div class="totals">
      <div class="row"><span>Generated On</span><span>${generatedOn}</span></div>
      <div class="row"><span>User ID</span><span>${safe(inv.userid)}</span></div>
      <div class="row"><span>${ChaafoDetails.name} GST</span><span>${ChaafoDetails.gst}</span></div>
    </div>
  </div>

  <div class="footer">${ChaafoDetails.Chaafotagline}</div>

  <div class="termsWrap">
    <div class="termsTitle">Terms & Conditions</div>
    <p class="termsText">
      This invoice represents the final settlement of completed restaurant orders for the stated period.
      All amounts are inclusive of applicable GST/SGST as per prevailing tax laws, and no further claims,
      adjustments, or disputes shall be entertained thereafter.
    </p>
  </div>

  <script>
    window.onload = function () {
      window.focus();
      window.print();
    };
  </script>

</div>
</body>
</html>`;

  const w = window.open("", "_blank");
  if (!w) {
    alert("Popup blocked. Please allow popups for this site.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
};



  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">
            Settlement Analytics — {clientName}
          </h1>
          <p className="text-sm text-gray-500">
            Filter, analyze, process & export
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 grid gap-2 sm:grid-cols-2 md:grid-cols-4">
        <label className="text-sm">
          <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
            <Calendar size={14} /> From
          </div>
          <input
            type="date"
            className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>
        <label className="text-sm">
          <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
            <Calendar size={14} /> To
          </div>
          <input
            type="date"
            className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
        <div className="flex items-end gap-2">
          <button
            className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
            onClick={applyFilter}
          >
            <RefreshCcw size={16} /> Filter
          </button>
          <button
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-rose-600 px-3 text-sm font-medium text-white hover:bg-rose-700"
            onClick={() => setConfirmProcess(true)}
          >
            <PlayCircle size={16} /> Process Settlement
          </button>
        </div>

        {/* NEW: Quick order-type settlement for yesterday */}
        <div className="flex items-end">
          <button
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#01C37D] bg-emerald-50/40 px-3 text-xs font-medium text-[#01C37D] hover:bg-emerald-50"
            onClick={() => setOrderTypeModalOpen(true)}
          >
            <PlayCircle size={16} />
            Order-Type Settlement (Yesterday)
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-1 text-xs text-gray-500">Total Order Amount</div>
          <div className="text-lg font-semibold">
            ₹ {fmt2(analytics?.totalOrderAmount || 0)}
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-1 text-xs text-gray-500">Platform Charges</div>
          <div className="text-lg font-semibold text-violet-700">
            ₹ {fmt2(analytics?.totalPlatformCharges || 0)}
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-1 text-xs text-gray-500">Gateway Charges</div>
          <div className="text-lg font-semibold text-amber-700">
            ₹ {fmt2(analytics?.totalGatewayCharges || 0)}
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-1 text-xs text-gray-500">Settlement Amount</div>
          <div className="text-lg font-semibold text-emerald-700">
            ₹ {fmt2(analytics?.totalSettlementAmount || 0)}
          </div>
        </div>
      </div>

      {/* Tabs (match order controller style) */}
      <div className="mt-5 mb-2 grid grid-cols-2 overflow-hidden rounded-2xl border bg-white md:w-fit">
        {[
          { key: "report", label: "Settlement Report" },
          { key: "history", label: "Processed History" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`border-b-4 px-3 py-2 text-sm font-semibold ${activeTab === t.key
                ? "border-[#01C37D] text-[#01C37D]"
                : "border-transparent text-gray-500"
              }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Active tab body */}
      <div className="grid gap-5">
        {activeTab === "report" && (
          <div className="rounded-2xl border bg-white p-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold">Settlement Report</div>
              <button
                className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
                onClick={() => setConfirmReportDownload(true)}
              >
                <Download size={16} /> Download Excel
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-[#01C37D] text-white">
                  <tr>
                    {[
                      "Date/Time",
                      "Customer",
                      "Mobile",
                      "Reference",
                      "Credit",
                      "Debit",
                      "Closing",
                      "Payment Mode",
                      "Settlement Status",
                      "Settlement Amount",
                      "Category",
                      "Charges",
                      "Tax GST",
                    ].map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && report.length === 0 && (
                    <tr>
                      <td
                        colSpan={13}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!loading && report.length === 0 && (
                    <tr>
                      <td
                        colSpan={13}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        No report data.
                      </td>
                    </tr>
                  )}
                  {report.map((r, i) => (
                    <tr key={`${r.refrence}-${i}`} className="border-t">
                      <td className="px-3 py-2">{r.datetime || "—"}</td>
                      <td className="px-3 py-2">{r.customer_name || "—"}</td>
                      <td className="px-3 py-2">{r.customer_mobile || "—"}</td>
                      <td className="px-3 py-2">{r.refrence || "—"}</td>
                      <td className="px-3 py-2">₹ {fmt2(r.credit)}</td>
                      <td className="px-3 py-2">₹ {fmt2(r.debit)}</td>
                      <td className="px-3 py-2">₹ {fmt2(r.closing)}</td>
                      <td className="px-3 py-2">
                        {r.payment_mode === "UPI" ? (
                          <Pill color="green" icon={Smartphone}>
                            UPI
                          </Pill>
                        ) : r.payment_mode === "cash" ? (
                          <Pill color="blue" icon={Banknote}>
                            Cash
                          </Pill>
                        ) : (
                          <Pill color="slate" icon={Clock4}>
                            {r.payment_mode || "—"}
                          </Pill>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.settlement_status === "Y" ? (
                          <Pill color="green" icon={CheckCircle2}>
                            Settled
                          </Pill>
                        ) : (
                          <Pill color="yellow" icon={Clock4}>
                            Pending
                          </Pill>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        ₹ {fmt2(r.settlement_amount)}
                      </td>
                      <td className="px-3 py-2">
                        {r.category === "incomplete_order" ? (
                          <Pill color="red" icon={AlertTriangle}>
                            Incomplete
                          </Pill>
                        ) : (
                          <Pill color="violet" icon={CheckCircle2}>
                            Order
                          </Pill>
                        )}
                      </td>
                      <td className="px-3 py-2">₹ {fmt2(r.charges)}</td>
                      <td className="px-3 py-2">{r.tax_gst ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div className="rounded-2xl border bg-white p-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold">
                Processed Settlement History
              </div>
              <button
                className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
                onClick={() => setConfirmHistoryDownload(true)}
              >
                <Download size={16} /> Download Excel
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-[#01C37D] text-white">
                  <tr>
                    {[
                      "ID",
                      "Amount",
                      "Order Amount",
                      "Message Charges",
                      "Platform Charge",
                      "Delivery Charge",
                      "Organization",
                      "User ID",
                      "To Date",
                      "From Date",
                      "Created On",
                      "Invoice",

                    ].map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr>
                      <td
                        colSpan={11}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        No history.
                      </td>
                    </tr>
                  )}
                  {history.map((h, i) => (
                    <tr key={`${h.id}-${i}`} className="border-t">
                      <td className="px-3 py-2">{h.id}</td>
                      <td className="px-3 py-2">₹ {fmt2(h.amount)}</td>
                      <td className="px-3 py-2">
                        ₹ {fmt2(h.orderAmount)}
                      </td>
                      <td className="px-3 py-2">
                        ₹ {fmt2(h.messageCharges)}
                      </td>
                      <td className="px-3 py-2">
                        ₹ {fmt2(h.platformCharges)}
                      </td>
                      <td className="px-3 py-2">
                        ₹ {fmt2(h.deliveryCharges)}
                      </td>
                      <td className="px-3 py-2">
                        {h.organization_name || "—"}
                      </td>
                      <td className="px-3 py-2">{h.userid || "—"}</td>
                      <td className="px-3 py-2">{h.to_date || "—"}</td>
                      <td className="px-3 py-2">{h.from_date || "—"}</td>
                      <td className="px-3 py-2">{h.created_on || "—"}</td>
                      <td className="px-3 py-2">
                        <button
                          className="inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => downloadSettlementInvoice(h)}
                          title="Download Settlement Invoice"
                        >
                          <Download size={14} /> Download
                        </button>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Confirms */}
      <Confirm
        open={confirmProcess}
        title={`Process Settlement for ${clientName}?`}
        desc={`Date range: ${fromDate} → ${toDate}. This will compute and move settlement amounts.`}
        confirmText="Process Now"
        danger
        onClose={() => setConfirmProcess(false)}
        onConfirm={processSettlementHandler}
      />
      <Confirm
        open={confirmReportDownload}
        title="Download Settlement Report?"
        desc={`Export rows for ${fromDate} → ${toDate}.`}
        confirmText="Download"
        onClose={() => setConfirmReportDownload(false)}
        onConfirm={() => {
          setConfirmReportDownload(false);
          downloadExcel(report, "SettlementReport");
        }}
      />
      <Confirm
        open={confirmHistoryDownload}
        title="Download Processed History?"
        desc={`Export processed settlements for ${fromDate} → ${toDate}.`}
        confirmText="Download"
        onClose={() => setConfirmHistoryDownload(false)}
        onConfirm={() => {
          setConfirmHistoryDownload(false);
          downloadExcel(history, "ProcessedHistory");
        }}
      />

      {/* NEW: Order-type settlement modal */}
      <Transition show={orderTypeModalOpen} as={Fragment}>
        <Dialog
          onClose={() => setOrderTypeModalOpen(false)}
          className="relative z-50"
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                <Dialog.Title className="text-base font-semibold">
                  Process Order-Type Settlement
                </Dialog.Title>
                <p className="mt-1 text-xs text-gray-500">
                  This will process settlement for{" "}
                  <span className="font-semibold text-gray-800">
                    yesterday ({yesterday})
                  </span>{" "}
                  only, for the selected order type.
                </p>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {ORDER_TYPES.map((t) => {
                    const Icon = t.icon;
                    const active = selectedOrderType === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setSelectedOrderType(t.key)}
                        className={`flex flex-col items-start rounded-2xl border p-3 text-left text-xs transition ${active
                            ? "border-[#01C37D] bg-emerald-50/60"
                            : "border-slate-200 hover:bg-slate-50"
                          }`}
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <span
                            className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] ${active
                                ? "bg-[#01C37D] text-white"
                                : "bg-slate-100 text-slate-700"
                              }`}
                          >
                            <Icon size={14} />
                          </span>
                          <span className="text-xs font-semibold">
                            {t.label}
                          </span>
                        </div>
                        <span className="text-[11px] text-gray-500">
                          {t.desc}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-5 flex items-center justify-between gap-2">
                  <div className="text-[11px] text-gray-500">
                    <span className="font-semibold text-gray-700">
                      Date locked:
                    </span>{" "}
                    {yesterday}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="h-9 rounded-xl border px-3 text-xs"
                      onClick={() => setOrderTypeModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#01C37D] px-3 text-xs font-medium text-white hover:bg-emerald-600"
                      onClick={processOrderTypeSettlementHandler}
                    >
                      <PlayCircle size={14} />
                      Process Now
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
